import AvtechProduct from '@avtechsoftware/ra-javascript-utilities/src/classes/AvtechProduct';
import {
  fromUserScaledNumberIncludingDelta,
} from '@avtechsoftware/ra-javascript-utilities/src/utilities/channels/ChannelValueRender';
import { OperatorTypeDetails, OperatorTypes } from '@avtechsoftware/ra-javascript-utilities/src/types/OperatorTypes';
import moment from 'moment';
import ChannelService from '@avtechsoftware/ra-javascript-utilities/src/utilities/channels/ChannelService';
import SingleAlert from '../SingleAlert';
import AlertAction from '../AlertAction';
import RaaChannelKey from '../RaaChannelKey';
import RaaThresholdRange from '../RaaThresholdRange';

const mapToThresholdRange = (range) => new RaaThresholdRange(
  range.id,
  range.typeId,
  range.thresholdRangeGroup ? range.thresholdRangeGroup.id : null,
  range.operatorId,
  range.subscriptionId,
  range.name,
  range.value,
  range.altValue,
  range.hysteresis,
  range.channelTypeId,
  range.customUnits,
  range.defaultColor,
  range.readingType,
  range.alertCount,
  range.metricsGroupCount,
);

const mapToProduct = (product) => new AvtechProduct(
  product.id,
  product.categoryId,
  product.name,
  product.shortProductNumber,
  product.productNumber,
  product.description,
  product.weight,
  product.crmId,
  product.primaryProductCode,
  product.price,
  product.sort,
  product.portTypeId,
);

const mapToSingleAlert = (data) => {
  if (!data || !data.alert) {
    return null;
  }
  let alertActionList = [];
  if (data.alertActions) {
    alertActionList = data.alertActionList.map((alertAction) => new AlertAction(
      alertAction.id,
      alertAction.type,
      alertAction.metaData,
      alertAction.isPrimaryAction,
      alertAction.reminderInterval,
      alertAction.maxReminders,
      alertAction.quietTimeList,
      alertAction.delay,
    ));
  }
  const additionalChannelKeys = data.additionalChannelKeys
    ? data.additionalChannelKeys.map((ck) => new RaaChannelKey(ck))
    : [];

  return new SingleAlert(
    data.id,
    data.description,
    data.triggeredAlertEvent,
    data.timezone,
    data.lastReading,
    data.triggerCondition,
    data.clearCondition,
    data.temperatureScale,
    alertActionList,
    data.isDeviceAssignmentSuspended,
    data.isAlertDeactivated,
    data.isAcknowledged,
    data.isFavoritedBySessionUser,
    data.channelKey,
    data.customTemplateList,
    data.customUnits,
    additionalChannelKeys,
    data.channel,
    data.hasAdvancedAlerting,
    data.tagIds,
  );
};

const mapSingleAlertV2 = (data) => {
  if (!data || !data.alert) {
    return null;
  }

  const {
    alert,
    channel,
    alertEvent,
    alertActions,
    isFavorite,
    additional_channel_keys: additionalChannelKeysRaw,
    tags,
    thresholdRange,
    channelKey,
    channelGroup,
    device,
    deviceAssignment,
  } = data;

  const additionalChannelKeys = additionalChannelKeysRaw
    ? additionalChannelKeysRaw.map((ck) => new RaaChannelKey(ck))
    : [];

  const tagIds = tags ? tags.map((tag) => tag.id) : [];

  const triggerOpDetails = OperatorTypeDetails[thresholdRange.operatorId];
  const triggerSymbol = triggerOpDetails ? triggerOpDetails.symbol : '';

  const triggeredAlertEvent = alertEvent ? {
    ...alertEvent,
    duration: alertEvent.duration || null,
    triggerTimestamp: alertEvent.startTime || null,
    acknowledgementTimestamp: moment.unix(alertEvent.acknowledgedAt).format('MMM D, YYYY h:mma z') || null,
    acknowledgementUser: {
      id: alertEvent.acknowledgedUserId,
      userFullName: alertEvent.acknowledgedUserFullName,
    }
      || { id: null, userFullName: null },
  } : {
    duration: null,
    triggerTimestamp: null,
    acknowledgementTimestamp: null,
    acknowledgementUser: { id: null, userFullName: null },
  };

  // Construct Trigger Condition
  const triggerCondition = {
    value: thresholdRange.value,
    operator: triggerSymbol,
    formattedValue: thresholdRange.value.toFixed(2),
    formattedCondition: `${triggerSymbol} ${thresholdRange.value.toFixed(2)}`,
  };

  // Calculate Clear Condition
  const reverseOpDetails = OperatorTypeDetails[triggerOpDetails.reverseOperator];
  const clearSymbol = reverseOpDetails ? reverseOpDetails.symbol : '';
  const hysteresis = 1;
  let clearValue = thresholdRange.value
        + (hysteresis * triggerOpDetails.hysteresisMultiplier);

  if (thresholdRange.operatorId === OperatorTypes.EQUAL_TO_BOOLEAN) {
    clearValue = Number(thresholdRange.value) === 1 ? 0 : 1;
  }

  const clearCondition = {
    value: clearValue,
    operator: clearSymbol,
    formattedValue: clearValue.toFixed(2),
    formattedCondition: `${clearSymbol} ${clearValue.toFixed(2)}`,
  };
  const mappedAlertActions = (alertActions || []).map((action) => ({
    repeatEnabled: !!action.repeatInterval,
    repeatInterval: action.repeatInterval,
    maxReminders: action.maxReminders,
    notificationGroupIdList: action.notificationGroupIds,
    ...action,
  }));
  const raaChannelKey = new RaaChannelKey(channelKey);

  const mappedChannel = channel ? {
    id: channel.id,
    type: channel.type,
    name: ChannelService.getChannelGroupChannel(
      raaChannelKey.channelGroupType, raaChannelKey.channelIndex, raaChannelKey.channelType,
    ).displayName,
    groupName: channelGroup ? channelGroup.name : 'Unknown Sensor',
    deviceName: device ? device.name : 'Unknown Device',
    deviceAssignmentId: deviceAssignment ? deviceAssignment.id : null,
    deviceId: deviceAssignment ? deviceAssignment.deviceId : null,
    unit: channel.unit,
  } : {};

  const lastReading = channel && channel.lastSampleValue !== null ? {
    formattedValue: `${Number(channel.lastSampleValue).toFixed(2)}°`,
    formattedTimestamp: moment.unix(channel.lastSampleTimestamp).format('MMM D, YYYY h:mma z'),
    value: channel.lastSampleValue.toString(),
  } : null;

  return new SingleAlert(
    alert.id,
    alert.description,
    triggeredAlertEvent,
    alert.timezone,
    lastReading,
    triggerCondition,
    clearCondition,
    alert.scale,
    mappedAlertActions,
    deviceAssignment ? deviceAssignment.isDeviceAssignmentSuspended : false,
    alert.deactivated,
    !!alertEvent && !!alertEvent.acknowledgedAt,
    isFavorite,
    channelKey,
    null, // custom template
    thresholdRange ? thresholdRange.customUnits : null,
    additionalChannelKeys,
    mappedChannel,
    null, // hasAdvancedAlerting - Should be grabbed from session
    tagIds,
  );
};

const mapFromUserScaledAlert = (newAlert) => {
  const newTriggerValue = fromUserScaledNumberIncludingDelta(
    newAlert.triggerCondition.value,
    newAlert.channelKey.channelType,
    newAlert.temperatureScale,
    (newAlert.channelKey.channelGroupType === 1500
      && newAlert.channelKey.channelIndex === 1),
  );
  const newClearValue = fromUserScaledNumberIncludingDelta(
    newAlert.clearCondition.value,
    newAlert.channelKey.channelType,
    newAlert.temperatureScale,
    (newAlert.channelKey.channelGroupType === 1500
      && newAlert.channelKey.channelIndex === 1),
  );
  const newTriggerCondition = { ...newAlert.triggerCondition, value: newTriggerValue };
  const newClearCondition = { ...newAlert.clearCondition, value: newClearValue };
  return {
    ...newAlert,
    channelKey: newAlert.channelKey.fullChannelKey,
    triggerCondition: newTriggerCondition,
    clearCondition: newClearCondition,
  };
};

export {
  mapToThresholdRange,
  mapToProduct,
  mapToSingleAlert,
  mapFromUserScaledAlert,
  mapSingleAlertV2,
};
